import React from 'react';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { Trash2, Minus, Plus, ShoppingCart, ArrowRight, Utensils, Package, Gift, ShoppingBag, ChefHat } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Cart = () => {
  const { cart, removeFromCart, updateQuantity, getTotalAmount, getItemCount } = useCart();
  const navigate = useNavigate();

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center relative overflow-hidden">
        {/* Background Decoration */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gold-500/5 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" />
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center p-8 relative z-10"
        >
          <div className="w-24 h-24 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-gold-500/10 border border-gold-500/20 relative group">
            <div className="absolute inset-0 bg-gold-500/20 rounded-full animate-ping opacity-20" />
            <ShoppingCart className="text-gold-500 w-10 h-10 group-hover:scale-110 transition-transform duration-300" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-3 font-serif">Your Cart is Empty</h2>
          <p className="text-gray-400 mb-8 max-w-sm mx-auto">Looks like you haven't added anything to your cart yet. Discover our delicious menu!</p>
          <Button
            onClick={() => navigate('/menu')}
            size="lg"
            className="bg-gold-500 text-black hover:bg-gold-400 font-bold px-8 py-6 rounded-full text-lg shadow-lg hover:shadow-gold-500/20 transition-all hover:scale-105"
          >
            <Utensils className="mr-2 h-5 w-5" /> Browse Menu
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20 relative overflow-hidden">
      {/* Background Gradients */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gold-900/10 rounded-full blur-3xl opacity-20" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-purple-900/10 rounded-full blur-3xl opacity-20" />
      </div>

      <div className="container mx-auto px-6 pt-40 pb-12 relative z-10">
        <div className="flex items-center gap-4 mb-8">
          <div className="p-3 bg-gold-500/10 rounded-xl">
            <ShoppingBag className="w-8 h-8 text-gold-500" />
          </div>
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white font-serif">Your Cart</h1>
            <p className="text-gray-400 text-sm">{getItemCount()} items ready for checkout</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-6">
            <AnimatePresence mode="popLayout">
              {cart.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ delay: index * 0.05 }}
                  layout
                >
                  <Card className="bg-gray-900/60 border-gray-800 backdrop-blur-sm overflow-hidden group hover:border-gold-500/30 transition-colors">
                    <CardContent className="p-6">
                      <div className="flex flex-col sm:flex-row gap-6">
                        {/* Item Image (if available, otherwise placeholder) */}
                        <div className="w-full sm:w-24 h-24 bg-gray-800 rounded-xl overflow-hidden flex-shrink-0 border border-white/5">
                          {item.image ? (
                            <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <ChefHat className="w-8 h-8 text-gray-600" />
                            </div>
                          )}
                        </div>

                        <div className="flex-1">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h3 className="text-xl font-bold text-white group-hover:text-gold-400 transition-colors cursor-pointer" onClick={() => navigate('/menu')}>{item.name}</h3>
                              {item.isDeal && (
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="bg-red-500/20 text-red-400 border border-red-500/30 px-2 py-0.5 rounded text-xs font-bold uppercase flex items-center gap-1">
                                    <Gift className="w-3 h-3" /> Deal
                                  </span>
                                </div>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeFromCart(index)}
                              className="text-gray-500 hover:text-red-500 hover:bg-red-500/10 -mt-2 -mr-2"
                            >
                              <Trash2 className="w-5 h-5" />
                            </Button>
                          </div>

                          {item.isDeal ? (
                            <div className="mb-3 space-y-2">
                              {item.items && (
                                <p className="text-sm text-gray-400 flex items-start gap-2 bg-gray-800/50 p-2 rounded-lg">
                                  <Package className="w-4 h-4 text-gold-500 mt-0.5 flex-shrink-0" />
                                  <span>{item.items}</span>
                                </p>
                              )}
                              <div className="flex items-center gap-3 text-sm">
                                <span className="text-gray-500 line-through decoration-red-500/50">Rs. {item.originalPrice?.toLocaleString()}</span>
                                <span className="text-green-500 font-bold">
                                  Save Rs. {(item.originalPrice - item.price)?.toLocaleString()}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-400 mb-3 font-medium">
                              Base Price: <span className="text-gold-100">Rs. {Math.round(item.price || 0).toLocaleString()}</span>
                            </p>
                          )}

                          {/* Options & Customization */}
                          {(item.selectedOptions.length > 0 || (item.selectedSettings && Object.keys(item.selectedSettings).length > 0)) && (
                            <div className="bg-gray-800/30 rounded-lg p-3 mb-4 text-sm space-y-2 border border-white/5">
                              {item.selectedOptions.length > 0 && (
                                <div>
                                  <span className="text-gray-500 block text-xs uppercase tracking-wider font-bold mb-1">Add-ons</span>
                                  <div className="flex flex-wrap gap-2">
                                    {item.selectedOptions.map((opt, i) => (
                                      <span key={i} className="inline-flex items-center bg-gold-900/20 text-gold-400 border border-gold-500/20 px-2 py-0.5 rounded text-xs">
                                        {opt.name} <span className="ml-1 opacity-75">+Rs.{Math.round(opt.additionalPrice)}</span>
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {item.selectedSettings && Object.keys(item.selectedSettings).length > 0 && (
                                <div>
                                  <span className="text-gray-500 block text-xs uppercase tracking-wider font-bold mb-1">Preferences</span>
                                  <div className="flex flex-wrap gap-2">
                                    {Object.entries(item.selectedSettings).map(([key, value], i) => (
                                      <span key={i} className="inline-flex items-center bg-purple-900/20 text-purple-400 border border-purple-500/20 px-2 py-0.5 rounded text-xs">
                                        <span className="opacity-75 mr-1">{key}:</span> {value}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          <div className="flex items-end justify-between mt-4 border-t border-white/5 pt-4">
                            <div className="flex items-center bg-gray-800 rounded-lg p-1 border border-gray-700">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => updateQuantity(index, item.quantity - 1)}
                                className="h-8 w-8 text-gray-400 hover:text-white hover:bg-gray-700"
                              >
                                <Minus className="w-3 h-3" />
                              </Button>
                              <span className="w-8 text-center text-white font-bold text-sm">{item.quantity}</span>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => updateQuantity(index, item.quantity + 1)}
                                className="h-8 w-8 text-gray-400 hover:text-white hover:bg-gray-700"
                              >
                                <Plus className="w-3 h-3" />
                              </Button>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-gray-500 mb-0.5">Subtotal</p>
                              <p className="text-xl font-bold text-gold-500">Rs. {Math.round(item.itemTotal || 0).toLocaleString()}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <Card className="bg-gray-900/80 border-gold-500/30 backdrop-blur-md shadow-xl shadow-black/50">
                <CardContent className="p-6">
                  <h2 className="text-xl font-bold text-white mb-6 font-serif border-b border-white/10 pb-4">Order Summary</h2>

                  <div className="space-y-4 mb-8">
                    <div className="flex justify-between text-gray-400">
                      <span>Subtotal ({getItemCount()} items)</span>
                      <span>Rs. {Math.round(getTotalAmount()).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-gray-400 text-sm">
                      <span>Delivery Fee</span>
                      <span className="text-gray-500 italic">Delivery calculated by Restaurant</span>
                    </div>

                    <div className="border-t border-white/10 pt-4 mt-4">
                      <div className="flex justify-between items-end">
                        <span className="text-lg font-bold text-white">Total</span>
                        <span className="text-2xl font-bold text-gold-500">Rs. {Math.round(getTotalAmount()).toLocaleString()}</span>
                      </div>
                      <p className="text-right text-xs text-gray-500 mt-1">PKR (Pakistani Rupee)</p>
                    </div>
                  </div>

                  <Button
                    onClick={() => navigate('/checkout')}
                    className="w-full bg-gradient-to-r from-gold-600 to-gold-400 text-black font-bold h-12 text-lg shadow-lg hover:shadow-gold-500/20 hover:scale-[1.02] transition-all mb-4"
                  >
                    Proceed to Checkout <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>

                  <Button
                    variant="outline"
                    onClick={() => navigate('/menu')}
                    className="w-full border-gray-700 text-gray-400 hover:bg-gray-800 hover:text-white"
                  >
                    Continue Shopping
                  </Button>

                  <div className="mt-6 flex items-center justify-center gap-2 text-xs text-gray-500 bg-black/20 p-3 rounded-lg">
                    <ShieldCheckIcon className="w-4 h-4" />
                    <span>Secure Checkout Guaranteed</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Simple Icon Component (since ShieldCheck is imported as ShieldCheckIcon in this specific instance to avoid naming collision if any)
const ShieldCheckIcon = (props) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

export default Cart;

